import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import {
  beginGoogleLogin,
  beginLichessLogin,
  login,
} from "@/pages/auth/api";
import { OAuthButton } from "@/pages/auth/OAuthButton";
import { getStoredUsername, persistSession } from "@/pages/auth/storage";
import { useI18n } from "@/shared/i18n/LanguageContext";

export function LoginPage() {
  const { copy } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { authStatus } = useParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionToken = params.get("session_token");
    const username = params.get("username");
    const provider = params.get("provider");
    const callbackError = params.get("error");

    if (authStatus === "error" && callbackError) {
      setError(callbackError);
      return;
    }

    if (authStatus === "success" && sessionToken && username) {
      persistSession({
        session_token: sessionToken,
        user: {
          user_id: "",
          username,
          primary_email: null,
          email_verified: false,
          avatar_url: null,
          created_at: new Date().toISOString(),
        },
      });
      navigate(`/@/${username}`, { replace: true });
      return;
    }

    const storedUsername = getStoredUsername();
    if (authStatus === "success" && !sessionToken && storedUsername) {
      navigate(`/@/${storedUsername}`, { replace: true });
      return;
    }

    if (provider && callbackError) {
      setError(`${provider}: ${callbackError}`);
    }
  }, [authStatus, location.search, navigate]);

  const submitLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const session = await login({
        email: email.trim(),
        password,
      });
      persistSession(session);
      navigate(`/@/${session.user.username}`);
    } catch (submitError) {
      setError(
        submitError instanceof Error ? submitError.message : copy.auth.loginFailed,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <form className="auth-panel" onSubmit={submitLogin}>
        <h1 className="auth-panel__title">{copy.auth.loginTitle}</h1>

        <div className="field-grid">
          <div className="field">
            <label htmlFor="login-email">{copy.auth.email}</label>
            <input
              id="login-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="login-password">{copy.auth.password}</label>
            <input
              id="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
        </div>

        {error ? <p className="auth-panel__error">{error}</p> : null}

        <div className="auth-panel__actions">
          <button
            className="button-link button-link--primary"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? copy.auth.signingIn : copy.auth.signIn}
          </button>

          <OAuthButton provider="google" onClick={beginGoogleLogin} />
          <OAuthButton provider="lichess" onClick={beginLichessLogin} />
        </div>

        <p className="auth-panel__linkline">
          {copy.auth.newUser} <Link to="/signup">{copy.auth.signupLink}</Link>
        </p>
      </form>
    </div>
  );
}
